import { useCallback, useEffect, useState } from "react";
import {
  cancelBarchartFinanceLogin,
  completeBarchartFinanceLogin,
  disconnectBarchartFinance,
  getBarchartFinanceStatus,
  saveBarchartFinanceCookies,
  startBarchartFinanceLogin,
} from "../api/barchartFinance";
import { getMessageForFetchError } from "../utils/apiError";

/**
 * Per-user Barchart session for Holdings watchlist refresh.
 */
export function useStockBarchartFinanceConnection({
  accountId,
  userId,
  enabled = true,
  onError,
  setConfirmDialog,
  onOpenPanel,
}) {
  const [connected, setConnected] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginSessionId, setLoginSessionId] = useState("");
  const [showManualImport, setShowManualImport] = useState(false);
  const [manualCookiesJson, setManualCookiesJson] = useState("");
  const [authNotice, setAuthNotice] = useState("");

  const refreshStatus = useCallback(async () => {
    if (!enabled || !accountId || !userId) return;
    try {
      const status = await getBarchartFinanceStatus(accountId, userId);
      setConnected(!!status.connected);
      setUpdatedAt(status.updatedAt || null);
    } catch (err) {
      onError?.(getMessageForFetchError(err));
    }
  }, [enabled, accountId, userId, onError]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleConnect = useCallback(async () => {
    if (!accountId || !userId) return;
    onOpenPanel?.();
    setLoading(true);
    setAuthNotice("");
    onError?.("");
    try {
      const { sessionId } = await startBarchartFinanceLogin(accountId, userId);
      setLoginSessionId(sessionId);
      setAuthNotice(
        "A browser window should open on the server for Barchart login. Sign in there, then click Save session below."
      );
    } catch (err) {
      setShowManualImport(true);
      onError?.(getMessageForFetchError(err));
    } finally {
      setLoading(false);
    }
  }, [accountId, userId, onError, onOpenPanel]);

  const handleCompleteLogin = useCallback(async () => {
    if (!accountId || !userId || !loginSessionId) return;
    setLoading(true);
    setAuthNotice("");
    onError?.("");
    try {
      await completeBarchartFinanceLogin(accountId, userId, loginSessionId);
      setLoginSessionId("");
      setAuthNotice("");
      setConnected(true);
      await refreshStatus();
    } catch (err) {
      onError?.(getMessageForFetchError(err));
    } finally {
      setLoading(false);
    }
  }, [accountId, userId, loginSessionId, onError, refreshStatus]);

  const handleCancelLogin = useCallback(async () => {
    if (loginSessionId && accountId && userId) {
      try {
        await cancelBarchartFinanceLogin(accountId, userId, loginSessionId);
      } catch (_) {}
    }
    setLoginSessionId("");
    setAuthNotice("");
  }, [loginSessionId, accountId, userId]);

  const handleDisconnect = useCallback(() => {
    if (!accountId || !userId) return;
    setConfirmDialog?.({
      open: true,
      title: "Disconnect Barchart",
      mode: "confirm",
      message:
        "Disconnect your Barchart session? You will need to connect again before refreshing holdings from Barchart.",
      onConfirm: async () => {
        setLoading(true);
        onError?.("");
        try {
          await disconnectBarchartFinance(accountId, userId);
          setConnected(false);
          setUpdatedAt(null);
          setLoginSessionId("");
          setAuthNotice("");
        } catch (err) {
          onError?.(getMessageForFetchError(err));
        } finally {
          setLoading(false);
        }
      },
    });
  }, [accountId, userId, onError, setConfirmDialog]);

  const handleSaveManualCookies = useCallback(async () => {
    if (!accountId || !userId) return;
    const raw = manualCookiesJson.trim();
    if (!raw) {
      onError?.("Paste a JSON array of Barchart cookies.");
      return;
    }
    setLoading(true);
    onError?.("");
    try {
      let cookies;
      try {
        cookies = JSON.parse(raw);
      } catch (_) {
        throw new Error("Cookies must be valid JSON (an array of cookie objects).");
      }
      await saveBarchartFinanceCookies(accountId, userId, cookies);
      setManualCookiesJson("");
      setShowManualImport(false);
      setConnected(true);
      await refreshStatus();
    } catch (err) {
      onError?.(getMessageForFetchError(err));
    } finally {
      setLoading(false);
    }
  }, [accountId, userId, manualCookiesJson, onError, refreshStatus]);

  return {
    connected,
    updatedAt,
    loading,
    loginSessionId,
    showManualImport,
    setShowManualImport,
    manualCookiesJson,
    setManualCookiesJson,
    authNotice,
    setAuthNotice,
    handleConnect,
    handleCompleteLogin,
    handleCancelLogin,
    handleDisconnect,
    handleSaveManualCookies,
  };
}
